import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query"
import {
	characterKeys,
	characterLastMessageQueryOptions,
} from "@/entities/characters"
import { friendsQueryOptions } from "@/entities/friend"
import { userQueryOptions } from "@/entities/user"
import { resolveOwner } from "./owner"
import { saveSnapshot, snapshotGeneration } from "./storage"

const WRITE_DELAY_MS = 1000

export function watchBootSnapshot(
	queryClient: QueryClient,
	userId: number,
): () => void {
	const generation = snapshotGeneration()
	const userKey = userQueryOptions(userId).queryKey
	const friendsKey = friendsQueryOptions(userId).queryKey
	const watched = new Set([hashKey(userKey), hashKey(friendsKey)])

	let owner: string | null = null
	let timer: ReturnType<typeof setTimeout> | undefined
	let stopped = false

	const write = () => {
		timer = undefined
		if (stopped || owner === null) return
		if (generation !== snapshotGeneration()) return

		const user = queryClient.getQueryData(userKey)
		const friends = queryClient.getQueryData(friendsKey)
		if (!user || !friends) return

		const lastMessageKey = characterLastMessageQueryOptions(
			user.character,
			userId,
		).queryKey
		const lastMessage = queryClient.getQueryData(lastMessageKey) ?? null
		saveSnapshot({ owner, user, friends, lastMessage })
	}

	// Превью любого персонажа: write() сам возьмёт текущего.
	const isLastMessage = ([scope, kind, , owner]: QueryKey) =>
		scope === characterKeys.all[0] &&
		kind === "last-message" &&
		owner === userId

	const schedule = () => {
		timer ??= setTimeout(write, WRITE_DELAY_MS)
	}

	void resolveOwner()
		.then((resolved) => {
			owner = resolved
			schedule()
		})
		.catch(() => {})

	const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
		if (event.type !== "updated" || event.action.type !== "success") return
		const { queryHash, queryKey } = event.query
		if (watched.has(queryHash) || isLastMessage(queryKey)) schedule()
	})

	return () => {
		stopped = true
		clearTimeout(timer)
		unsubscribe()
	}
}
